import { Controller } from "@hotwired/stimulus";
import {
    clearSidebarCollapsedTooltips,
    refreshSidebarCollapsedTooltips,
} from "../utils/sidebar_collapsed_tooltips";
import { applySidebarWidthPx, SIDEBAR_WIDTH_DEFAULT } from "../utils/sidebar_width_sync";

const STORAGE_KEY = "depozen:sidebar-layout";
const OPENED = "opened";
const COLLAPSED = "collapsed";
const XL_BREAKPOINT = 1280;

export default class extends Controller {
    static values = {
        collapseLabel: String,
        expandLabel: String,
    };

    connect() {
        window.addEventListener("storage", this.boundSyncLayoutFromStorage);
        this.element.addEventListener("depozen:sidebar-collapse-from-resize", this.boundCollapseFromResize);
        this.element.addEventListener("depozen:sidebar-expand-from-resize", this.boundExpandFromResize);

        const layout = this.readLayout();
        this.applyLayout(layout, { persist: false });

        refreshSidebarCollapsedTooltips();
    }

    disconnect() {
        window.removeEventListener("storage", this.boundSyncLayoutFromStorage);
        this.element.removeEventListener("depozen:sidebar-collapse-from-resize", this.boundCollapseFromResize);
        this.element.removeEventListener("depozen:sidebar-expand-from-resize", this.boundExpandFromResize);
    }

    boundSyncLayoutFromStorage = (event) => this.syncLayoutFromStorage(event);
    boundCollapseFromResize = () => this.collapseFromResize();
    boundExpandFromResize = () => this.expandFromResize();

    toggle() {
        const next = this.currentLayout() === OPENED ? COLLAPSED : OPENED;

        if (next === OPENED) {
            applySidebarWidthPx(SIDEBAR_WIDTH_DEFAULT, { persist: true, instant: false });
            this.element.classList.remove("app-shell--sidebar-narrow");
        }

        this.applyLayout(next, { persist: true });

        if (next === COLLAPSED) {
            refreshSidebarCollapsedTooltips();
        } else {
            clearSidebarCollapsedTooltips();
        }
    }

    collapseFromResize() {
        if (this.currentLayout() === COLLAPSED) return;

        this.applyLayout(COLLAPSED, { persist: true });
        refreshSidebarCollapsedTooltips();
    }

    expandFromResize() {
        if (this.currentLayout() === OPENED) return;

        this.applyLayout(OPENED, { persist: true });
        clearSidebarCollapsedTooltips();
    }

    readLayout() {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored === OPENED || stored === COLLAPSED) return stored;

        return window.matchMedia(`(min-width: ${XL_BREAKPOINT}px)`).matches ? OPENED : COLLAPSED;
    }

    currentLayout() {
        return this.element.dataset.sidebarLayout === COLLAPSED ? COLLAPSED : OPENED;
    }

    applyLayout(layout, { persist }) {
        if (layout !== OPENED && layout !== COLLAPSED) return;

        this.element.dataset.sidebarLayout = layout;
        document.documentElement.dataset.sidebarLayout = layout;
        window.__DEPOZEN_SIDEBAR_LAYOUT__ = layout;

        if (layout === OPENED) clearSidebarCollapsedTooltips();

        if (persist) {
            localStorage.setItem(STORAGE_KEY, layout);
        }

        this.updateToggleButton(layout);
        document.dispatchEvent(new CustomEvent("depozen:sidebar-layout-change"));
    }

    syncLayoutFromStorage(event) {
        if (event.key !== STORAGE_KEY) return;

        const layout = event.newValue === OPENED || event.newValue === COLLAPSED ? event.newValue : this.readLayout();
        if (layout === this.currentLayout()) return;

        this.applyLayout(layout, { persist: false });

        if (layout === COLLAPSED) {
            refreshSidebarCollapsedTooltips();
        }
    }

    updateToggleButton(layout) {
        const button = this.element.querySelector(".sidebar-layout-toggle");
        if (!button) return;

        const collapsed = layout === COLLAPSED;
        const label = collapsed ? this.expandLabelValue : this.collapseLabelValue;
        button.setAttribute("aria-label", label);
    }
}
